import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from "react";

import { Button } from "@/components/ui";
import { findAnchor, visibleTourSteps, type TourStep } from "@/lib/onboarding";

import { useOnboarding } from "./OnboardingProvider";

const WIDTH = 320;
const GAP = 12;

/**
 * The first-run tour: a few short steps pointing at the menu, the business switcher, the checklist and the help
 * menu. It is a card beside what it describes, not a modal: no scrim, no trap, the page stays usable. Keyboard:
 * focus moves into the card at each step, Tab reaches Skip, Back and Next, Escape skips. It never starts over a
 * dialog, and once skipped or finished the server remembers it for the person, in every browser.
 */
export function Tour() {
  const onboarding = useOnboarding();
  if (!onboarding?.tourOpen) return null;
  return <TourCard onEnd={onboarding.endTour} />;
}

function TourCard({ onEnd }: { onEnd: (outcome: "completed" | "skipped") => void }) {
  // The steps whose anchors are on screen when the tour starts (the sidebar is not, on a phone).
  const [steps] = useState<TourStep[]>(() => visibleTourSteps());
  const [index, setIndex] = useState(0);
  const step = steps[index];
  const last = index === steps.length - 1;
  const card = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const bodyId = useId();
  const [target, setTarget] = useState<DOMRect | null>(null);

  // Focus goes into the card on each step and returns to where it was when the tour ends: or, when that is gone
  // (the help menu item that started it) or was nowhere, to the help button.
  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    return () => {
      const back = before && before !== document.body && document.contains(before) ? before : null;
      (back ?? document.querySelector<HTMLElement>('[data-tour="help-menu"] button'))?.focus();
    };
  }, []);
  useEffect(() => {
    card.current?.focus();
  }, [index]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onEnd("skipped");
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onEnd]);

  // Where the step's target is, kept current as the page scrolls or resizes.
  const anchor = step?.anchor;
  useLayoutEffect(() => {
    const measure = () => setTarget(findAnchor(anchor)?.getBoundingClientRect() ?? null);
    findAnchor(anchor)?.scrollIntoView?.({ block: "nearest" });
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [anchor]);

  if (!step) return null;
  return (
    <>
      {target && (
        <div
          aria-hidden
          className="pointer-events-none fixed z-[59] rounded-xl ring-2 ring-brand-500 ring-offset-2"
          style={{ top: target.top, left: target.left, width: target.width, height: target.height }}
        />
      )}
      <div
        ref={card}
        role="dialog"
        aria-modal="false"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        tabIndex={-1}
        style={place(target)}
        className="fixed z-[60] flex max-w-[calc(100vw-24px)] flex-col gap-3 rounded-xl border border-border bg-surface p-4 shadow-lg outline-none print:hidden"
      >
        <p className="text-xs font-medium text-fg-subtle">
          Step {index + 1} of {steps.length}
        </p>
        <h2 id={titleId} className="text-base font-semibold text-fg">
          {step.title}
        </h2>
        <p id={bodyId} className="text-sm text-fg-muted">
          {step.body}
        </p>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => onEnd("skipped")}>
            Skip tour
          </Button>
          <span className="flex-1" />
          {index > 0 && (
            <Button variant="secondary" size="sm" onClick={() => setIndex(index - 1)}>
              Back
            </Button>
          )}
          <Button size="sm" onClick={() => (last ? onEnd("completed") : setIndex(index + 1))}>
            {last ? "Done" : "Next"}
          </Button>
        </div>
      </div>
    </>
  );
}

/** Beside the sidebar's items, under the header's, otherwise in the middle of the screen. */
function place(target: DOMRect | null): CSSProperties {
  const width = Math.min(WIDTH, window.innerWidth - 2 * GAP);
  if (!target) return { width, top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  const beside = target.right < 360 && target.height > 60;
  const left = beside
    ? target.right + GAP
    : Math.min(Math.max(target.left, GAP), window.innerWidth - width - GAP);
  const top = beside ? Math.max(GAP, Math.min(target.top, window.innerHeight - 220)) : target.bottom + GAP;
  return { width, top, left };
}
